import DoneIcon from '@mui/icons-material/Done';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import { Chip, Grid, styled } from '@mui/material';
import { LuExternalLink } from 'react-icons/lu';
import { MdDoNotDisturbOn } from 'react-icons/md';
import { ICandidate } from 'wisestep-api';
import {
  WsColors,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  Button,
  WsButton,
  getSocialIconByURL,
  WsElementWithPopOver,
  WsIconCopy,
  WsChip,
  WsChipVariants,
  WsIconText,
  BriefcaseBusinessIcon,
  BuildingIcon,
} from 'wisestep-base';

import { success } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../../../utils/openUrlInNewTab';
import { copyStringToClipboard } from '../CandidateDetailsCard/Helper';
import { dncTypes } from '../topBanner';

interface IMergeCandidateBar {
  candidate?: ICandidate;
  handleDifferent: () => void;
  mergeCandidate: () => void;
}

const Img = styled('img')(() => ({
  height: '25px',
  width: '25px',
  cursor: 'pointer',
  marginRight: '7px',
}));

const MergeCandidateBar = ({ candidate, handleDifferent, mergeCandidate }: IMergeCandidateBar) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const candidateDncType = candidate?.dnc?.dataPrivacyOption ?? 'open';

  const copy = (value) => {
    copyStringToClipboard(value);
    setNotification(success('Copied Successfully !!'));
  };
  const isInDnc = (candidate?.dnc?.isDeleted ?? false) || (candidate?.dnc?.isInDnc ?? false);

  return (
    <Grid
      px={1}
      py={2}
      sx={{ backgroundColor: WsColors.TheBlues900, boxShadow: 10 }}
      borderRadius={2}
      container
      display="flex"
      justifyContent="flex-start"
      alignItems="flex-start"
      columnGap={0}
      direction={'row'}
    >
      <Grid item xs={8} px={1}>
        <Grid
          item
          justifyContent="flex-start"
          spacing={1}
          alignItems="center"
          direction={'row'}
          display="flex"
        >
          {candidate?.profileURLs && candidate.profileURLs[0] && (
            <Grid item mt={1}>
              <WsElementWithPopOver
                element={<Img src={getSocialIconByURL(candidate?.profileURLs[0])} />}
              >
                <Grid container direction="column" p={1.5}>
                  <Grid item>
                    <WsButton
                      startIcon={<LuExternalLink color={WsColors.TertiaryShades800} />}
                      onClick={() => openUrlInNewTab(candidate?.profileURLs?.[0] ?? '')}
                      size="small"
                      variant="text"
                    >
                      Open In a New Tab
                    </WsButton>
                  </Grid>
                  <Grid item>
                    <WsButton
                      startIcon={<WsIconCopy />}
                      onClick={() => copy(candidate?.profileURLs?.[0] ?? '')}
                      variant="text"
                      size="small"
                    >
                      Copy Link
                    </WsButton>
                  </Grid>
                </Grid>
              </WsElementWithPopOver>
            </Grid>
          )}
          <Grid item>
            <WsTypography color={WsTypoColors.WHITE} variant={WsTypoVariants.TITLE} bold>
              {candidate?.name}
            </WsTypography>
          </Grid>
          {isInDnc && (
            <>
              <Grid item ml={2}>
                <WsChip
                  icon={<MdDoNotDisturbOn color={WsColors.Red400} />}
                  value="Do Not Contact"
                  variant={WsChipVariants.INVALID}
                />
              </Grid>
              <Grid item ml={2}>
                <Chip
                  icon={<SellOutlinedIcon fontSize="small" />}
                  sx={{ borderRadius: 1, backgroundColor: WsColors.White }}
                  label={dncTypes.get(candidateDncType)}
                  size="medium"
                />
              </Grid>
            </>
          )}
          {!isInDnc && candidate?.email && (
            <Grid item ml={3}>
              <WsElementWithPopOver
                element={
                  <WsIconText
                    icon={<EmailOutlinedIcon style={{ color: WsColors.White, fontSize: 20 }} />}
                    text={candidate?.email ?? ''}
                    textSize={WsTypoVariants.BODY1}
                    textColor={WsTypoColors.WHITE}
                    textCursor="pointer"
                  />
                }
              >
                <Grid container direction="column" p={1.5}>
                  <Grid item>
                    <WsButton
                      startIcon={<WsIconCopy />}
                      onClick={() => copy(candidate?.email ?? '')}
                      variant="text"
                      size="small"
                    >
                      Copy
                    </WsButton>
                  </Grid>
                </Grid>
              </WsElementWithPopOver>
            </Grid>
          )}
          {!isInDnc && candidate?.phoneNumber && (
            <Grid item ml={3}>
              <WsElementWithPopOver
                element={
                  <WsIconText
                    icon={
                      <LocalPhoneOutlinedIcon style={{ color: WsColors.White, fontSize: 20 }} />
                    }
                    text={candidate?.phoneNumber ?? ''}
                    textSize={WsTypoVariants.BODY1}
                    textColor={WsTypoColors.WHITE}
                    textCursor="pointer"
                  />
                }
              >
                <Grid container direction="column" p={1.5}>
                  <Grid item>
                    <WsButton
                      startIcon={<WsIconCopy />}
                      onClick={() => copy(candidate?.phoneNumber ?? '')}
                      variant="text"
                      size="small"
                    >
                      Copy
                    </WsButton>
                  </Grid>
                </Grid>
              </WsElementWithPopOver>
            </Grid>
          )}

          {candidate?.personalWebsite && (
            <Grid item mt={1}>
              <PersonIcon
                fontSize="medium"
                color="primary"
                onClick={() => openUrlInNewTab(candidate?.personalWebsite ?? '')}
              />
            </Grid>
          )}
        </Grid>
        <Grid item marginLeft={1}>
          <WsIconText
            icon={<BriefcaseBusinessIcon stroke={WsColors.White} />}
            text={candidate?.currentJobTitle ?? ''}
            textSize={WsTypoVariants.BODY1}
            textColor={WsTypoColors.WHITE}
            textCursor="pointer"
          />
        </Grid>
        <Grid item marginLeft={1}>
          <WsIconText
            icon={<BuildingIcon stroke={WsColors.White} />}
            text={candidate?.currentEmployer ?? ''}
            textSize={WsTypoVariants.BODY1}
            textColor={WsTypoColors.WHITE}
            textCursor="pointer"
          />
        </Grid>
        <Grid item marginLeft={1}>
          <WsIconText
            icon={<FmdGoodOutlinedIcon style={{ color: WsColors.White, fontSize: 20 }} />}
            text={`${candidate?.currentLocation?.city ?? ''}, 
                    ${candidate?.currentLocation?.state ?? ''},
                    ${candidate?.currentLocation?.country ?? ''}`}
            textSize={WsTypoVariants.BODY1}
            textColor={WsTypoColors.WHITE}
            textCursor="pointer"
          />
        </Grid>
      </Grid>

      <Grid
        item
        xs={4}
        justifyContent="flex-end"
        spacing={1}
        pr={1}
        alignItems="center"
        direction={'row'}
        display="flex"
        columnGap={2}
      >
        <Grid item>
          <Button
            variant="outlined"
            onClick={handleDifferent}
            sx={{
              whiteSpace: 'nowrap',
            }}
            size="large"
            startIcon={<DoNotDisturbIcon />}
          >
            Different
          </Button>
        </Grid>
        <Grid item>
          <WsButton
            className="submit-candidate"
            variant="contained"
            startIcon={<DoneIcon />}
            onClick={() => mergeCandidate()}
            size="large"
          >
            Same
          </WsButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MergeCandidateBar;
